import { ref, onUnmounted } from 'vue';
import axios from 'axios';

/**
 * Construye la URL de coruscant según el entorno actual.
 * Mismo patrón que app.factory.ts (mb_admin).
 *   local  → http://localhost:3010
 *   dev    → https://coruscant.motoboychile.dev
 *   prod   → https://coruscant.motoboychile.app
 */
function getCoruscantUrl(): string {
    if (typeof window === 'undefined') return 'http://localhost:3010';
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:3010';
    if (host.endsWith('.dev')) return 'https://coruscant.motoboychile.dev';
    return 'https://coruscant.motoboychile.app';
}

const API_URL = import.meta.env.VITE_API_URL ?? getCoruscantUrl();

export interface TrackingTimeline {
    status: string;
    label: string;
    at: string;
}

export interface TrackingData {
    track_code: string;
    status: string;
    service_type: string;
    origin: { comuna: string | null; lat: number | null; lng: number | null };
    destination: { comuna: string | null; lat: number | null; lng: number | null };
    timeline: TrackingTimeline[];
    driver_position: {
        latitude: number;
        longitude: number;
        updated_at: string;
    } | null;
    driver: {
        name: string;
    } | null;
    is_active: boolean;
}

const SERVICE_TYPE_LABELS: Record<string, string> = {
    base: 'Servicio Base',
    normal: 'Normal',
    express: 'Express',
    same_day: 'Same Day',
    food_delivery: 'Food Delivery',
    first_mile: 'First Mile',
    last_mile: 'Last Mile',
    people_transport: 'Transporte',
};

export function useTracking() {
    const trackingData = ref<TrackingData | null>(null);
    const isLoading = ref(false);
    const error = ref<string | null>(null);
    const notFound = ref(false);
    let pollingTimer: ReturnType<typeof setInterval> | null = null;

    async function fetchTracking(trackCode: string): Promise<boolean> {
        if (!trackCode || trackCode.trim().length < 3) {
            error.value = 'Ingresa un código de seguimiento válido';
            return false;
        }

        isLoading.value = true;
        error.value = null;
        notFound.value = false;

        try {
            const response = await axios.get(`${API_URL}/tracking/${encodeURIComponent(trackCode.trim())}`);

            if (response.data?.success && response.data?.data) {
                trackingData.value = response.data.data;
                // Iniciar polling si la tarea está activa
                if (trackingData.value?.is_active) {
                    startPolling(trackCode);
                } else {
                    stopPolling();
                }
                return true;
            } else {
                notFound.value = true;
                trackingData.value = null;
                return false;
            }
        } catch (err: any) {
            if (err.response?.data?.message) {
                error.value = err.response.data.message;
                if (err.response.data.message.includes('no encontrado')) {
                    notFound.value = true;
                }
            } else {
                error.value = 'Error de conexión. Intenta nuevamente.';
            }
            trackingData.value = null;
            return false;
        } finally {
            isLoading.value = false;
        }
    }

    function startPolling(trackCode: string) {
        stopPolling();
        pollingTimer = setInterval(async () => {
            try {
                const response = await axios.get(`${API_URL}/tracking/${encodeURIComponent(trackCode.trim())}`);
                if (response.data?.success && response.data?.data) {
                    trackingData.value = response.data.data;
                    if (!trackingData.value?.is_active) {
                        stopPolling();
                    }
                }
            } catch {
                // Silenciar errores de polling
            }
        }, 30000); // Cada 30 segundos
    }

    function stopPolling() {
        if (pollingTimer) {
            clearInterval(pollingTimer);
            pollingTimer = null;
        }
    }

    function getServiceLabel(type: string | undefined): string {
        if (!type) return 'Servicio';
        return SERVICE_TYPE_LABELS[type] || type;
    }

    function formatTimestamp(ts: string): string {
        if (!ts) return '';
        const date = new Date(ts);
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();

        const time = date.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });

        if (isToday) {
            return `Hoy, ${time}`;
        }
        return `${date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit' })}, ${time}`;
    }

    onUnmounted(() => {
        stopPolling();
    });

    return {
        trackingData,
        isLoading,
        error,
        notFound,
        fetchTracking,
        stopPolling,
        getServiceLabel,
        formatTimestamp,
    };
}
